import React from 'react';
import { DollarSign, Users, TrendingUp, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface StrategyCardProps {
  title: string;
  yourShare: string;
  rebate: string;
  description: string;
  delay: string;
}

const StrategyCard: React.FC<StrategyCardProps> = ({
  title, yourShare, rebate, description, delay
}) => {
  return (
    <div
      className="bg-[#0F1C33] rounded-xl p-6 transition-all duration-300 animate-on-scroll hover:shadow-[0_0_30px_rgba(18,198,232,0.15)]"
      style={{ transitionDelay: delay }}
    >
      <h4 className="font-heading text-lg font-bold mb-4 text-center">{title}</h4>
      <div className="space-y-3 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-text-secondary text-sm">You Keep:</span>
          <span className="text-gradient-from font-bold text-xl">{yourShare}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-text-secondary text-sm">Rebate:</span>
          <span className="text-[#4FFFB0] font-bold text-xl">{rebate}</span>
        </div>
      </div>
      <p className="text-text-secondary text-sm text-center">{description}</p>
    </div>
  );
};

interface StepProps {
  number: number;
  text: string;
  delay: string;
}

const Step: React.FC<StepProps> = ({ number, text, delay }) => {
  return (
    <div
      className="flex items-start gap-4 animate-on-scroll"
      style={{ transitionDelay: delay }}
    >
      <div className="w-8 h-8 rounded-full bg-accent-gradient flex items-center justify-center flex-shrink-0 animate-glowPulse">
        <span className="font-heading font-bold text-sm">{number}</span>
      </div>
      <p className="text-text-secondary pt-1">{text}</p>
    </div>
  );
};

const ReferralProgram: React.FC = () => {
  const { t } = useTranslation();

  const strategies = [
    {
      title: t('referralProgram.strategy1.title'),
      yourShare: t('referralProgram.strategy1.yourShare'),
      rebate: t('referralProgram.strategy1.rebate'),
      description: t('referralProgram.strategy1.description')
    },
    {
      title: t('referralProgram.strategy2.title'),
      yourShare: t('referralProgram.strategy2.yourShare'),
      rebate: t('referralProgram.strategy2.rebate'),
      description: t('referralProgram.strategy2.description')
    },
    {
      title: t('referralProgram.strategy3.title'),
      yourShare: t('referralProgram.strategy3.yourShare'),
      rebate: t('referralProgram.strategy3.rebate'),
      description: t('referralProgram.strategy3.description')
    }
  ];

  const steps = [
    t('referralProgram.step1'),
    t('referralProgram.step2'),
    t('referralProgram.step3'),
    t('referralProgram.step4'),
    t('referralProgram.step5')
  ];

  return (
    <section id="referral" className="section-spacing bg-bg-primary relative">
      <div className="container-custom">
        <div className="text-center mb-16 animate-on-scroll">
          <div className="inline-flex items-center gap-3 mb-4">
            <DollarSign className="text-[#4FFFB0]" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold">
              {t('referralProgram.heading')}
            </h2>
          </div>
          <p className="text-text-secondary text-lg max-w-3xl mx-auto">
            {t('referralProgram.subtitle')}
          </p>
        </div>

        {/* Smart Strategy Tip */}
        <div className="glass-card p-6 mb-12 animate-on-scroll border-l-4 border-[#4FFFB0]">
          <div className="flex items-start gap-4">
            <TrendingUp className="text-[#4FFFB0] flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-heading font-bold mb-2">{t('referralProgram.smartStrategy.title')}</h3>
              <p className="text-text-secondary">
                {t('referralProgram.smartStrategy.text')}
              </p>
            </div>
          </div>
        </div>

        {/* Strategy Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {strategies.map((strategy, index) => (
            <StrategyCard
              key={index}
              title={strategy.title}
              yourShare={strategy.yourShare}
              rebate={strategy.rebate}
              description={strategy.description}
              delay={`${0.1 * (index + 1)}s`}
            />
          ))}
        </div>

        {/* How It Works */}
        <div className="glass-card p-8 mb-12 animate-on-scroll">
          <div className="flex items-center gap-3 mb-8">
            <Users className="text-gradient-from" size={28} />
            <h3 className="text-2xl font-bold font-heading">
              {t('referralProgram.howItWorks')}
            </h3>
          </div>

          <div className="space-y-6 max-w-3xl">
            {steps.map((step, index) => (
              <Step
                key={index}
                number={index + 1}
                text={step}
                delay={`${0.1 * (index + 1)}s`}
              />
            ))}
          </div>
        </div>

        {/* Critical Note */}
        <div className="glass-card p-6 mb-12 animate-on-scroll bg-[#0F1C33] border border-[#4FFFB0]/30">
          <div className="flex items-start gap-4">
            <div className="w-6 h-6 rounded-full bg-[#4FFFB0] flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-[#0A1324] font-bold text-sm">!</span>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-2 text-[#4FFFB0]">
                {t('referralProgram.critical.title')}
              </h4>
              <p className="text-text-secondary">
                {t('referralProgram.critical.text')}
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center animate-on-scroll py-8">
          <p className="text-white text-xl md:text-2xl font-semibold mb-2 max-w-3xl mx-auto leading-relaxed">
            {t('referralProgram.cta.text')}
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <svg className="w-6 h-6 text-[#4FFFB0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
            <span className="text-[#4FFFB0] font-medium">{t('referralProgram.cta.chatIconHint')}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReferralProgram;
